import { useEffect, useState } from 'react';
import { Avatar, Box, ButtonBase, Drawer, Typography, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import NavSection from '../../Components/Sidebar/NavSection';
import Scrollbar from '../../Components/Scrollbar';
import { knownRoutes } from '../../Routes/routes';
import useAuth from '../../Context/Hooks/useCustomAuth';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import { eventAPI } from '../../API/eventAPI';
import { hasPermission } from '../../Utils/permissions';
import { SIDEBAR_WIDTH, TOPBAR_HEIGHT } from '../layoutConstants';

export { SIDEBAR_WIDTH };

// ─── Initials helper ─────────────────────────────────────────────────────────

const getInitials = (name) => {
  if (!name) return 'JC';
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ─── Profile footer ───────────────────────────────────────────────────────────

const SidebarUserStrip = ({ name, role }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const avatarBg = theme.palette.brand?.sidebarAvatarBg ?? '#5E8CB1';
  const nameColor = theme.palette.brand?.sidebarNameColor ?? '#858685';
  const subtitleColor = theme.palette.brand?.sidebarSubtitleColor ?? '#8F8F8F';
  const hairline = theme.palette.brand?.sidebarHairline ?? '#F6F6F6';

  return (
    <ButtonBase
      onClick={() => navigate('/Gateway/Profile')}
      sx={{
        borderTop: `1px solid ${hairline}`,
        px: 1.5,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        bgcolor: 'background.paper',
        flexShrink: 0,
        width: '100%',
        textAlign: 'left',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: avatarBg,
          fontSize: '0.65rem',
          fontWeight: 700,
          flexShrink: 0,
          color: '#FFFFFF',
        }}
      >
        {getInitials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: 1.3,
            color: nameColor,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {name || 'Joana Costa'}
        </Typography>
        <Typography
          sx={{ fontSize: '0.6875rem', color: subtitleColor, lineHeight: 1.25 }}
        >
          {role || 'Admin Gateway'}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

SidebarUserStrip.propTypes = {
  name: PropTypes.string,
  role: PropTypes.string,
};

// ─── Notification badge pill ──────────────────────────────────────────────────

const NavBadge = ({ count, color }) => (
  <Box
    sx={{
      bgcolor: color,
      color: '#FFFFFF',
      borderRadius: '50%',
      minWidth: 18,
      height: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.6rem',
      fontWeight: 700,
      lineHeight: 1,
      px: 0.5,
      flexShrink: 0,
    }}
  >
    {count > 99 ? '99+' : count}
  </Box>
);

NavBadge.propTypes = {
  count: PropTypes.number,
  color: PropTypes.string,
};

// ─── Sidebar ─────────────────────────────────────────────────────────────────

const DashboardSidebar = ({ onCloseSidebar, openSidebar }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedClient } = useGatewayContagem();

  const [criticalCount, setCriticalCount] = useState(0);

  useEffect(() => {
    let active = true;
    if (!selectedClient?.id) {
      setCriticalCount(0);
      return;
    }

    const fetchCount = async () => {
      try {
        const result = await eventAPI.getCriticalCount(selectedClient.id);
        if (active && result && !result.error) {
          setCriticalCount(result.value ?? 0);
        }
      } catch (err) {
        console.error('Error fetching critical events count:', err);
      }
    };

    fetchCount();

    // Listen to manual triggers (e.g. event resolution/comments changes)
    window.addEventListener('gateway-events-updated', fetchCount);

    // Refresh every 30 seconds
    const interval = setInterval(fetchCount, 30000);

    return () => {
      active = false;
      window.removeEventListener('gateway-events-updated', fetchCount);
      clearInterval(interval);
    };
  }, [selectedClient?.id]);

  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));

  const userRole = user?.role ?? 'USER';
  const userPermissions = user?.permissions ?? [];
  const userName = user?.name ?? '';

  const sidebarBorder = theme.palette.brand?.sidebarBorder ?? '#F0F2F1';
  const sectionLabelColor = theme.palette.brand?.sidebarSectionLabel ?? '#AAABAD';
  const badgeColor = theme.palette.brand?.navItemBadge ?? '#DD4957';

  // Compute visible sidebar items — filter restricted routes, then orphan section labels
  const sidebarItems = (() => {
    const flat = knownRoutes.filter(x => x.title !== undefined || x.sectionLabel !== undefined);
    const isAccessible = (r) => {
      if (r.allowedRoles && !r.allowedRoles.includes(userRole)) return false;
      if (r.requiredPermission && !hasPermission(userPermissions, r.requiredPermission)) return false;
      return true;
    };
    const withAccess = flat.map(r => ({
      ...r,
      _visible: r.sectionLabel !== undefined || isAccessible(r),
    }));
    return withAccess.filter((r, idx, arr) => {
      if (!r.sectionLabel) return r._visible;
      const nextLabel = arr.findIndex((x, i) => i > idx && x.sectionLabel !== undefined);
      const end = nextLabel === -1 ? arr.length : nextLabel;
      return arr.slice(idx + 1, end).some(x => x._visible);
    });
  })();

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Nav items */}
      <Box sx={{ flex: '1 1 auto', overflow: 'hidden' }}>
        <Scrollbar options={{ suppressScrollX: true }}>
          <Box sx={{ px: 1, pt: 1.5, pb: 2 }}>
            {sidebarItems.map((route, idx) => {
                // ── Section label (OPERAÇÃO / ADMINISTRAÇÃO) ──
                if (route.sectionLabel) {
                  return (
                    <Box
                      key={`section-${idx}`}
                      sx={{ pt: idx === 0 ? 0.5 : 2.5, pb: 0.5, px: 1.25 }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          color: sectionLabelColor,
                          lineHeight: 1.4,
                          display: 'block',
                        }}
                      >
                        {t(route.sectionLabel)}
                      </Typography>
                    </Box>
                  );
                }

                // ── Nav item ──
                const badge = route.badge && criticalCount > 0 ? (
                  <NavBadge
                    count={criticalCount}
                    color={badgeColor}
                  />
                ) : null;

                return (
                  <NavSection
                    key={route.title + '_' + idx}
                    title={route.title}
                    path={route.path}
                    icon={route.icon}
                    info={badge}
                    _children={route.children}
                  />
                );
              })}
          </Box>
        </Scrollbar>
      </Box>

      {/* Profile footer */}
      <SidebarUserStrip
        name={userName}
        role={user?.jobTitle}
      />
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      onClose={onCloseSidebar}
      open={openSidebar}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          width: SIDEBAR_WIDTH,
          marginTop: lgDown ? 0 : `${TOPBAR_HEIGHT}px`,
          height: lgDown ? '100%' : `calc(100% - ${TOPBAR_HEIGHT}px)`,
          borderRight: `1px solid ${sidebarBorder}`,
          boxShadow: 'none',
        },
      }}
      variant={lgDown ? 'temporary' : 'persistent'}
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onCloseSidebar: PropTypes.func,
  openSidebar: PropTypes.bool,
};

export default DashboardSidebar;
